"use client"

import { useState, useEffect } from "react"
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Container,
  Avatar,
  BottomNavigation,
  BottomNavigationAction,
  Card,
  CardContent,
  TextField,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from "@mui/material"
import {
  Payment as PaymentIcon,
  Home as HomeIcon,
  CalendarToday as CalendarTodayIcon,
  Notifications as NotificationsIcon,
  AccountCircle as AccountCircleIcon,
  Calculate as CalculateIcon,
  History as HistoryIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  AttachMoney as AttachMoneyIcon,
} from "@mui/icons-material"
import { useNavigate } from "react-router-dom"
import { toast, ToastContainer } from "react-toastify"
import "react-toastify/dist/ReactToastify.css"
import ExitToAppIcon from "@mui/icons-material/ExitToApp"
import axios from "axios"

const AdminListeFactures = () => {
  const navigate = useNavigate()
  const [invoices, setInvoices] = useState([])
  const [searchQuery, setSearchQuery] = useState("")
  const [openEditModal, setOpenEditModal] = useState(false)
  const [selectedInvoice, setSelectedInvoice] = useState(null)
  const [loading, setLoading] = useState(false)
  const token = localStorage.getItem("CC_Token")
  const baseUrl = process.env.REACT_APP_APP_URL
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [invoiceToUpdate, setInvoiceToUpdate] = useState(null)

  // New state variables for user selection and total calculation
  const [users, setUsers] = useState([])
  const [selectedUser, setSelectedUser] = useState("")
  const [totalToPay, setTotalToPay] = useState(0)
  const [showTotal, setShowTotal] = useState(false)

  // New state variables for client history popup
  const [openHistoryDialog, setOpenHistoryDialog] = useState(false)
  const [selectedClientHistory, setSelectedClientHistory] = useState([])
  const [selectedClientInfo, setSelectedClientInfo] = useState(null)

  useEffect(() => {
    const fetchInvoices = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/invoices`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setInvoices(response.data)
      } catch (error) {
        console.error("Error fetching invoices:", error)
        toast.error("Erreur lors de la récupération des factures.", {
          position: "top-right",
          autoClose: 3000,
        })
      }
    }

    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${baseUrl}/api/users`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        })
        setUsers(response.data)
      } catch (error) {
        console.error("Error fetching users:", error)
        toast.error("Erreur lors de la récupération des utilisateurs.", {
          position: "top-right",
          autoClose: 3000,
        })
      }
    }

    fetchInvoices()
    fetchUsers()
  }, [token, baseUrl])

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value)
  }

  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs")
  }

  const handleLogout = () => {
    localStorage.removeItem("CC_Token")
    localStorage.removeItem("user")
    toast.success("Vous avez été déconnecté avec succès.", {
      position: "top-right",
      autoClose: 3000,
    })
    setTimeout(() => navigate("/login"), 4000)
  }

  const handleEditInvoice = (invoice) => {
    console.log("[Modifier Facture] Opening modal – invoice from list:", {
      id: invoice.id,
      invoice_number: invoice.invoice_number,
    })
    setSelectedInvoice(invoice)
    setOpenEditModal(true)
  }

  const handleCloseModal = () => {
    setOpenEditModal(false)
    setSelectedInvoice(null)
  }

  const handleSubmitEdit = async () => {
    // Send only the fields the backend validates — never send the nested user object
    const payload = {
      invoice_number: selectedInvoice.invoice_number,
      amount: selectedInvoice.amount,
      due_date: selectedInvoice.due_date,
      status: selectedInvoice.status,
    }

    try {
      const response = await axios.put(`${baseUrl}/api/invoices/${selectedInvoice.id}`, payload, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // L'API renvoie la facture dans response.data.invoice
      const responseInvoice = response.data?.invoice ?? response.data

      // Merge API response with local edits so the list reflects updated values immediately
      const mergedItem = { ...selectedInvoice, ...responseInvoice }

      const updatedInvoices = invoices.map((invoice) =>
        invoice.id === selectedInvoice.id ? mergedItem : invoice,
      )

      // Also update the history dialog's snapshot so it reflects the change immediately
      // without requiring a page refresh
      setSelectedClientHistory((prev) =>
        prev.map((invoice) => (invoice.id === selectedInvoice.id ? mergedItem : invoice)),
      )

      toast.success("Facture mise à jour avec succès.", {
        position: "top-right",
        autoClose: 3000,
      })

      setInvoices(updatedInvoices)
      setOpenEditModal(false)
      setSelectedInvoice(null)
    } catch (error) {
      console.error("[Modifier Facture] SAVE – Error updating invoice:", error)
      toast.error("Erreur lors de la mise à jour de la facture.", {
        position: "top-right",
        autoClose: 3000,
      })
    }
  }

  // New function to handle status change directly from the table
  const handleStatusChangeClick = (invoice) => {
    if (invoice.status === "À payer") {
      setInvoiceToUpdate(invoice)
      setOpenConfirmDialog(true)
    }
  }

  const handleConfirmStatusChange = async () => {
    setLoading(true)
    try {
      // Create updated invoice object
      const updatedInvoice = {
        ...invoiceToUpdate,
        status: "Payé",
      }

      // Send update to API
      const response = await axios.put(`${baseUrl}/api/invoices/${invoiceToUpdate.id}`, updatedInvoice, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      // Update local state
      const updatedInvoices = invoices.map((invoice) =>
        invoice.id === invoiceToUpdate.id ? { ...invoice, status: "Payé" } : invoice,
      )
      setInvoices(updatedInvoices)

      toast.success("La facture a été marquée comme payée.", {
        position: "top-right",
        autoClose: 3000,
      })
    } catch (error) {
      console.error("Error updating invoice status:", error)
      toast.error("Erreur lors de la mise à jour du statut de la facture.", {
        position: "top-right",
        autoClose: 3000,
      })
    } finally {
      setLoading(false)
      setOpenConfirmDialog(false)
      setInvoiceToUpdate(null)
    }
  }

  const handleCancelStatusChange = () => {
    setOpenConfirmDialog(false)
    setInvoiceToUpdate(null)
  }

  // New function to calculate total amount to pay for selected user
  const calculateTotalToPay = () => {
    if (!selectedUser) {
      toast.error("Veuillez sélectionner un utilisateur.", {
        position: "top-right",
        autoClose: 3000,
      })
      return
    }

    // Filter invoices for the selected user
    const userInvoices = invoices.filter((invoice) => invoice.user.id === Number.parseInt(selectedUser))

    // Calculate total using the same formula as in payment.js
    const totalPayer = userInvoices
      .filter((invoice) => invoice.status.toLowerCase() === "à payer")
      .reduce((sum, invoice) => sum + Number.parseFloat(invoice.amount), 0)

    const totalAcompte = userInvoices
      .filter((invoice) => invoice.status.toLowerCase() === "acompte")
      .reduce((sum, invoice) => sum + Number.parseFloat(invoice.amount), 0)

    const total = totalPayer - totalAcompte

    setTotalToPay(total)
    setShowTotal(true)
  }

  const handleUserChange = (e) => {
    setSelectedUser(e.target.value)
    setShowTotal(false) // Hide total when user changes
  }

  // New function to get client statistics
  const getClientStatistics = () => {
    const clientStats = {}
    
    invoices.forEach((invoice) => {
      const clientId = invoice.user.id
      const clientName = invoice.user.name
      const clientEmail = invoice.user.email
      
      if (!clientStats[clientId]) {
        clientStats[clientId] = {
          id: clientId,
          name: clientName,
          email: clientEmail,
          totalInvoices: 0,
          totalAmount: 0,
          paidAmount: 0,
          unpaidAmount: 0,
          acompteAmount: 0,
          cancelledAmount: 0,
          invoices: []
        }
      }
      
      clientStats[clientId].totalInvoices++
if (invoice.status !== "Acompte") {
  clientStats[clientId].totalAmount += Number.parseFloat(invoice.amount);
}
      clientStats[clientId].invoices.push(invoice)
      
      switch (invoice.status.toLowerCase()) {
        case "payé":
          clientStats[clientId].paidAmount += Number.parseFloat(invoice.amount);

          break
        case "à payer":
          clientStats[clientId].unpaidAmount += Number.parseFloat(invoice.amount)
          break
        case "acompte":
          clientStats[clientId].acompteAmount += Number.parseFloat(invoice.amount)
          break
        case "annulé":
          clientStats[clientId].cancelledAmount += Number.parseFloat(invoice.amount)
          break
      }

    }
  )
    return Object.values(clientStats)
  }

  // New function to handle client history view
  const handleViewClientHistory = (client) => {
    setSelectedClientInfo(client)
    setSelectedClientHistory(client.invoices)
    setOpenHistoryDialog(true)
  }

  const handleCloseHistoryDialog = () => {
    setOpenHistoryDialog(false)
    setSelectedClientHistory([])
    setSelectedClientInfo(null)
  }

  // Get client statistics
  const clientStatistics = getClientStatistics()

  // Filter clients based on search query
  const filteredClients = clientStatistics.filter((client) => {
    const lowercasedQuery = searchQuery.toLowerCase()
    return (
      client.name.toLowerCase().includes(lowercasedQuery) ||
      client.email.toLowerCase().includes(lowercasedQuery)
    )
  })

  const handleNavigateToCreateInvoice = () => {
    navigate("/AdminPaiment")
  }

  const handleProfile = () => {
    navigate("/ProfileAdmin")
  }

  const getStatusColor = (status) => {
    switch (status.toLowerCase()) {
      case "payé":
        return "#4CAF50" // Green
      case "à payer":
        return "#f44336" // Red
      case "acompte":
        return "#ff9800" // Orange
      case "annulé":
        return "#9e9e9e" // Grey
      default:
        return "#000000" // Black
    }
  }

  return (
    <div className="app">
      {/* Toast Container */}
      <ToastContainer />

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar alt="Logo" src={"../assets/cej.png"} sx={{ width: 40, height: 40, marginRight: "0.5rem" }} />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box
        sx={{
          padding: "1rem",
          paddingTop: "90px", // Espace pour le header
          paddingBottom: "70px", // Espace pour le footer
          backgroundColor: "#f5f5f5",
          minHeight: "100vh",
        }}
      >
        <Container maxWidth="sm">
          {/* Calculateur de total */}
          <Card sx={{ boxShadow: 3, borderRadius: 2, mb: 3 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: "#003366", fontWeight: "bold", textAlign: "center" }}>
                Calculer le total à payer
              </Typography>
              <Stack spacing={2}>
                <FormControl fullWidth>
                  <InputLabel id="user-select-label">Sélectionner un utilisateur</InputLabel>
                  <Select
                    labelId="user-select-label"
                    value={selectedUser}
                    onChange={handleUserChange}
                    label="Sélectionner un utilisateur"
                  >
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.name} ({user.email})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  onClick={calculateTotalToPay}
                  startIcon={<CalculateIcon />}
                  disabled={!selectedUser}
                >
                  Calculer le total
                </Button>
              </Stack>

              {showTotal && (
                <Box sx={{ mt: 3, p: 2, bgcolor: "#e3f2fd", borderRadius: 1, textAlign: "center" }}>
                  <Typography variant="h6" sx={{ color: "#003366" }}>
                    Total à payer: <span style={{ fontWeight: "bold" }}>{totalToPay.toFixed(2)} DT</span>
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Section principale des clients */}
          <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h5" sx={{ marginBottom: "1.5rem", textAlign: "center", color: "#003366", fontWeight: "bold" }}>
                Clients et Factures
              </Typography>

              {/* Search Bar */}
              <TextField
                label="Rechercher un client"
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={handleSearchChange}
                sx={{ marginBottom: "1rem" }}
              />

              <Button
                variant="contained"
                color="primary"
                sx={{ marginBottom: "1rem", width: "100%" }}
                onClick={handleNavigateToCreateInvoice}
              >
                Créer une nouvelle facture
              </Button>

              {/* Liste des clients en cartes */}
              <Stack spacing={2}>
                {filteredClients.map((client) => (
                  <Card 
                    key={client.id} 
                    sx={{ 
                      border: "1px solid #e0e0e0",
                      "&:hover": { 
                        boxShadow: 4,
                        backgroundColor: "#f8f9fa"
                      }
                    }}
                  >
                    <CardContent sx={{ pb: 1 }}>
                      {/* En-tête client */}
                      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                        <Avatar sx={{ bgcolor: "#003366", mr: 2 }}>
                          <PersonIcon />
                        </Avatar>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" sx={{ fontWeight: "bold", color: "#003366" }}>
                            {client.name}
                          </Typography>
                          <Typography variant="body2" sx={{ color: "#666", display: "flex", alignItems: "center" }}>
                            <EmailIcon sx={{ fontSize: 16, mr: 0.5 }} />
                            {client.email}
                          </Typography>
                        </Box>
                      </Box>

                      {/* Statistiques en grille */}
                      <Grid container spacing={1} sx={{ mb: 2 }}>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: "#e3f2fd", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>Factures</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#003366" }}>
                              {client.totalInvoices}
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: "#f3e5f5", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>Total</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#003366" }}>
                              {client.totalAmount.toFixed(2)} DT
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: "#e8f5e8", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>Payé</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#4CAF50" }}>
{(client.paidAmount + client.acompteAmount).toFixed(2)} DT
                            </Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={6}>
                          <Box sx={{ textAlign: "center", p: 1, bgcolor: "#ffebee", borderRadius: 1 }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>À Payer</Typography>
                            <Typography variant="h6" sx={{ fontWeight: "bold", color: "#f44336" }}>
                              {(client.unpaidAmount - client.acompteAmount).toFixed(2)} DT
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>

                      {/* Bouton d'action */}
                      <Button
                        variant="contained"
                        color="info"
                        fullWidth
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewClientHistory(client)}
                        sx={{
                          backgroundColor: "#2196F3",
                          "&:hover": {
                            backgroundColor: "#1976D2",
                          },
                        }}
                      >
                        Voir Historique des Factures
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Container>
      </Box>

      {/* Footer */}
      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
          <BottomNavigationAction
            label="Home"
            icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/Admin")}
          />
          <BottomNavigationAction
            label="Paiement"
            icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminPaiment")}
          />
        </BottomNavigation>
      </Box>

      {/* Client History Dialog - Optimisé Mobile */}
      <Dialog 
        open={openHistoryDialog} 
        onClose={handleCloseHistoryDialog}
        fullScreen
        PaperProps={{
          sx: {
            backgroundColor: "#f5f5f5",
          },
        }}
      >
        <AppBar sx={{ position: "relative", backgroundColor: "#003366" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={handleCloseHistoryDialog}
              aria-label="close"
            >
              <ExitToAppIcon />
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              Historique - {selectedClientInfo?.name}
            </Typography>
          </Toolbar>
        </AppBar>
        
        <Box sx={{ p: 2 }}>
          {selectedClientInfo && (
            <>
              {/* Informations client */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#003366", mb: 2, textAlign: "center" }}>
                    Informations Client
                  </Typography>
                  <Stack spacing={1}>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography><strong>Nom:</strong></Typography>
                      <Typography>{selectedClientInfo.name}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography><strong>Email:</strong></Typography>
                      <Typography>{selectedClientInfo.email}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography><strong>Total Factures:</strong></Typography>
                      <Chip label={selectedClientInfo.totalInvoices} color="primary" size="small" />
                    </Box>
                  </Stack>
                </CardContent>
              </Card>

              {/* Résumé financier */}
              <Card sx={{ mb: 2 }}>
                <CardContent>
                  <Typography variant="h6" sx={{ color: "#003366", mb: 2, textAlign: "center" }}>
                    Résumé Financier
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, bgcolor: "#e3f2fd", borderRadius: 1 }}>
                        <Typography variant="body2">Total</Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                          {selectedClientInfo.totalAmount.toFixed(2)} DT
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ textAlign: "center", p: 1, bgcolor: "#e8f5e8", borderRadius: 1 }}>
                        <Typography variant="body2">Payé</Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#4CAF50" }}>
                          {selectedClientInfo.paidAmount.toFixed(2)} DT
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12}>
                      <Box sx={{ textAlign: "center", p: 1, bgcolor: "#ffebee", borderRadius: 1 }}>
                        <Typography variant="body2">Reste à Payer</Typography>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#f44336" }}>
                          {(selectedClientInfo.unpaidAmount - selectedClientInfo.acompteAmount).toFixed(2)} DT
                        </Typography>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
              
              {/* Liste des factures */}
              <Typography variant="h6" sx={{ mb: 2, color: "#003366", textAlign: "center" }}>
                Détail des Factures
              </Typography>
              
              <Stack spacing={2}>
                {selectedClientHistory.map((invoice) => (
                  <Card key={invoice.id} sx={{ border: "1px solid #e0e0e0" }}>
                    <CardContent sx={{ pb: 1 }}>
                      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                        <Typography variant="h6" sx={{ fontWeight: "bold", color: "#003366" }}>
                          {invoice.invoice_number}
                        </Typography>
                        <Chip
                          label={invoice.status}
                          sx={{
                            backgroundColor: getStatusColor(invoice.status),
                            color: "#ffffff",
                            fontWeight: "bold",
                          }}
                          size="small"
                        />
                      </Box>
                      
                      <Stack spacing={1}>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography><strong>Montant:</strong></Typography>
                          <Typography sx={{ fontWeight: "bold" }}>{invoice.amount} DT</Typography>
                        </Box>
                        <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                          <Typography><strong>Échéance:</strong></Typography>
                          <Typography>{invoice.due_date}</Typography>
                        </Box>
                      </Stack>

                      <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                        <Button
                          variant="outlined"
                          size="small"
                          fullWidth
                          onClick={() => handleEditInvoice(invoice)}
                        >
                          Modifier
                        </Button>
                        {invoice.status === "À payer" && (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            fullWidth
                            onClick={() => handleStatusChangeClick(invoice)}
                            disabled={loading}
                          >
                            Marquer Payé
                          </Button>
                        )}
                      </Stack>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </Box>
      </Dialog>

      {/* Edit Invoice Modal */}
      {selectedInvoice && (
        <Dialog open={openEditModal} onClose={handleCloseModal} fullWidth maxWidth="sm">
          <DialogTitle>Modifier la Facture</DialogTitle>
          <DialogContent>
            {/* invoice_number stores the description/label of the invoice */}
            <TextField
              label="Numéro / Description de la Facture"
              value={selectedInvoice.invoice_number}
              onChange={(e) =>
                setSelectedInvoice({ ...selectedInvoice, invoice_number: e.target.value })
              }
              fullWidth
              margin="normal"
            />
            {/* Display-only: user info cannot be changed via invoice update */}
            <TextField
              label="Utilisateur"
              value={selectedInvoice.user?.name ?? ""}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
              helperText="Le nom du client ne peut pas être modifié ici."
            />
            <TextField
              label="Email"
              value={selectedInvoice.user?.email ?? ""}
              fullWidth
              margin="normal"
              InputProps={{ readOnly: true }}
            />
            <TextField
              label="Montant"
              type="number"
              value={selectedInvoice.amount}
              onChange={(e) =>
                setSelectedInvoice({
                  ...selectedInvoice,
                  amount: e.target.value,
                })
              }
              fullWidth
              margin="normal"
            />
            {/* Statut Combo Box */}
            <FormControl fullWidth margin="normal">
              <InputLabel>Statut</InputLabel>
              <Select
                value={selectedInvoice.status}
                onChange={(e) =>
                  setSelectedInvoice({
                    ...selectedInvoice,
                    status: e.target.value,
                  })
                }
                label="Statut"
              >
                <MenuItem value="Payé">Payé</MenuItem>
                <MenuItem value="À payer">À payer</MenuItem>
                <MenuItem value="Annulé">Annulé</MenuItem>
                <MenuItem value="Acompte">Acompte</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Date d'échéance"
              type="date"
              value={selectedInvoice.due_date ?? ""}
              onChange={(e) =>
                setSelectedInvoice({
                  ...selectedInvoice,
                  due_date: e.target.value,
                })
              }
              fullWidth
              margin="normal"
              InputLabelProps={{ shrink: true }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseModal}>Annuler</Button>
            <Button onClick={handleSubmitEdit} color="primary" variant="contained">
              Sauvegarder
            </Button>
          </DialogActions>
        </Dialog>
      )}
      
      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={handleCancelStatusChange}
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: "400px",
            p: 2,
          },
        }}
      >
        <DialogTitle sx={{ color: "#f44336" }}>⚠️ Confirmation</DialogTitle>
        <DialogContent>
          <Typography>Êtes-vous sûr de vouloir marquer cette facture comme payée ?</Typography>
          <Typography
            sx={{
              mt: 2,
              color: "#f44336",
              fontWeight: "bold",
            }}
          >
            Cette action est irréversible !
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, pt: 0 }}>
          <Button onClick={handleCancelStatusChange} color="inherit" variant="outlined">
            Annuler
          </Button>
          <Button
            onClick={handleConfirmStatusChange}
            color="primary"
            variant="contained"
            disabled={loading}
            sx={{
              backgroundColor: "#4CAF50",
              "&:hover": {
                backgroundColor: "#45a049",
              },
            }}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default AdminListeFactures
